import { useAppSelector, useAppDispatch } from '@/store' // Import your Redux hooks
import { clearUser, setCurrentUser } from '@/store/currentUserSlice' // Import the actions
import { setHasSession, clearSession } from '@/store/sessionSlice' // Import session actions
import { User } from '@/types'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useRouter } from '@/i18n/routing'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'

// Singleton state to prevent multiple redirects and fetches
const globalState = {
  isRedirecting: false,
  lastFetchTime: 0,
  isFetching: false,
}

// Debounce delay for fetch operations (in ms)
const FETCH_DEBOUNCE_DELAY = 1000

// Minimum time between fetches (in ms)
const MIN_FETCH_INTERVAL = 5000

// Auto-refresh interval (in ms)
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

// Custom hook to access and manage current user
export const useCurrentUser = () => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const t = useTranslations('Auth')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  // Select currentUser and session state from Redux store
  const currentUser = useAppSelector((state) => state.user.currentUser)
  const hasSession = useAppSelector((state) => state.session?.hasSession || false)

  // Ref mirror: fetchCurrentUser must stay referentially stable (effects and
  // debounced wrappers key on its identity), but its 401 branch needs the
  // CURRENT "did we ever have a session" state, not the value captured at
  // callback creation.
  const hadSessionRef = useRef(false)
  hadSessionRef.current = hasSession || !!currentUser

  // Handle session expiration (shared across all instances)
  const handleSessionExpired = useCallback(() => {
    // Check global state to prevent multiple redirects
    if (globalState.isRedirecting) {
      return // Already handling expiration
    }

    // Don't show "session expired" if already on sign-in page
    if (typeof window !== 'undefined' && window.location.pathname.includes('/sign-in')) {
      return
    }

    globalState.isRedirecting = true

    // Clear user state and session indicator
    dispatch(clearUser())
    dispatch(clearSession())

    // Clear the refresh interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Show notification (only once)
    toast.error(t('sessionExpired'))

    // Clear any remaining session data
    setError('Session expired')

    // Redirect to sign-in page
    setTimeout(() => {
      router.push('/sign-in')
      // Reset redirect flag after navigation
      setTimeout(() => {
        globalState.isRedirecting = false
      }, 1000)
    }, 100) // Small delay to ensure state updates complete
  }, [dispatch, router, t])

  // Fetch fresh user data from the server (memoized)
  const fetchCurrentUser = useCallback(async (force = false) => {
    // Don't fetch if we're already redirecting
    if (globalState.isRedirecting) {
      return null
    }

    // Prevent concurrent fetches across all instances
    if (globalState.isFetching && !force) {
      return null
    }

    // Implement minimum fetch interval to prevent spam
    const now = Date.now()
    if (!force && now - globalState.lastFetchTime < MIN_FETCH_INTERVAL) {
      return null
    }

    globalState.isFetching = true
    globalState.lastFetchTime = now

    // Only set loading state if component is still mounted
    if (isMountedRef.current) {
      setLoading(true)
      setError(null)
    }

    try {
      const response = await fetch(force ? '/api/auth/current-user?force=true' : '/api/auth/current-user')

      if (response.ok) {
        const data = await response.json()
        if (data.data) {
          dispatch(setCurrentUser(data.data))
          dispatch(setHasSession(true)) // Mark that we have a valid session
          globalState.isRedirecting = false // Reset redirect flag on success
          return data.data
        }
      } else if (response.status === 401) {
        // Only show "session expired" if user previously had a session
        // Don't show it on first visit or when there was never a session
        if (hadSessionRef.current) {
          handleSessionExpired()
        }
      } else {
        if (isMountedRef.current) {
          setError('Failed to fetch user data')
        }
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err)
      if (isMountedRef.current) {
        setError('Network error while fetching user data')
      }
    } finally {
      globalState.isFetching = false
      if (isMountedRef.current) {
        setLoading(false)
      }
    }

    return null
  }, [dispatch, handleSessionExpired])

  // Debounced fetch function
  const debouncedFetchCurrentUser = useCallback((force = false) => {
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    // Don't debounce forced fetches
    if (force) {
      return fetchCurrentUser(true)
    }

    // Debounce non-forced fetches
    fetchTimeoutRef.current = setTimeout(() => {
      fetchCurrentUser(false)
    }, FETCH_DEBOUNCE_DELAY)
  }, [fetchCurrentUser])

  // Action to clear the user from Redux store
  const clearUserState = useCallback(() => {
    dispatch(clearUser())
    // Clear the interval when user is cleared
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [dispatch])

  // Action to set a new user in Redux store
  const setUserState = useCallback((user: User) => {
    dispatch(setCurrentUser(user))
  }, [dispatch])

  // Auto-refresh user data on mount and periodically
  useEffect(() => {
    isMountedRef.current = true

    // Don't auto-fetch on sign-in page - user is logged out
    const isOnSignInPage = typeof window !== 'undefined' && window.location.pathname.includes('/sign-in')

    // Fetch user data if we have a session indicator but no user data
    const initFetch = async () => {
      // Skip auto-fetch on sign-in page
      if (isOnSignInPage) {
        return
      }

      // If we have a session indicator but no user data (or a rehydrated user
      // without its auth token — the JWT is never persisted to storage), fetch.
      // This happens after a page refresh (session persisted, user data not)
      if (hasSession && (!currentUser || !currentUser.token) && !globalState.isRedirecting) {
        // We likely have a valid session, fetch user data
        await fetchCurrentUser(true)
      } else if (!hasSession && !currentUser && !globalState.isRedirecting) {
        // No session indicator and no user - might be first load
        // Try fetching to see if we have a valid httpOnly cookie
        await fetchCurrentUser(true)
      }

      // Set up periodic refresh
      // Only one instance should set up the interval
      if (!intervalRef.current && currentUser) {
        intervalRef.current = setInterval(() => {
          // Only refresh if not redirecting
          if (!globalState.isRedirecting) {
            debouncedFetchCurrentUser(false)
          }
        }, AUTO_REFRESH_INTERVAL)
      }
    }

    initFetch()

    // Also refresh on window focus (debounced)
    const handleFocus = () => {
      // Only refresh if we have user data and not redirecting
      if (currentUser && !globalState.isRedirecting) {
        debouncedFetchCurrentUser(false)
      }
    }

    window.addEventListener('focus', handleFocus)

    // Cleanup
    return () => {
      isMountedRef.current = false
      window.removeEventListener('focus', handleFocus)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
        fetchTimeoutRef.current = null
      }
    }
  }, [currentUser, hasSession, fetchCurrentUser, debouncedFetchCurrentUser])

  // Return the currentUser, session state, and actions
  return {
    currentUser,
    hasSession, // Indicates if we likely have a valid session
    clearUser: clearUserState,
    setUser: setUserState,
    fetchCurrentUser: debouncedFetchCurrentUser,
    loading,
    error
  }
}